import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdClose } from "react-icons/io";
import { MdTableBar } from "react-icons/md";
import { Skeleton } from "../ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { StandaloneTable } from "@/types/globalTypes";
import { ArabicOrdinal, EnglishOrdinal } from "@/types/enums";
import axios from "axios";
import toast from "react-hot-toast";
import { LiaChairSolid } from "react-icons/lia";

type CashierTableListProps = {
  selectedTable: StandaloneTable | null;
  setSelectedTable: Dispatch<SetStateAction<StandaloneTable | null>>;
};

type CashierTableCardProps = {
  table: StandaloneTable;
  floorNumber: number | string;
  isSelected: boolean;
  onSelect: () => void;
};

function CashierTableList({
  selectedTable,
  setSelectedTable,
}: CashierTableListProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [tables, setTables] = useState<StandaloneTable[]>([]);
  const [modalSelectedTable, setModalSelectedTable] =
    useState<StandaloneTable | null>(selectedTable);
  const lang = i18n.language;
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const selectedBranch = useSelector((state: RootState) => state.branch.branch);

  const ordinalFloor =
    i18n.language === "ar"
      ? ArabicOrdinal[Number(selectedTable?.floorNumber) - 1] ||
        selectedTable?.floorNumber
      : EnglishOrdinal[Number(selectedTable?.floorNumber) - 1] ||
        selectedTable?.floorNumber;

  const handleOpenModal = () => {
    setModalSelectedTable(selectedTable);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = () => {
    if (modalSelectedTable) {
      setSelectedTable(modalSelectedTable);
      setIsModalOpen(false);
    }
  };

  const handleTableSelect = (tableId: StandaloneTable) => {
    setModalSelectedTable(tableId === modalSelectedTable ? null : tableId);
  };

  useEffect(() => {
    if (!token) return;
    let url;
    if (selectedBranch) {
      url = `${BASE_URL}/api/dashboard/floors/tables?locale=${lang}&branch=${selectedBranch?._id}`;
    } else {
      url = `${BASE_URL}/api/dashboard/floors/tables?locale=${lang}`;
    }
    setIsLoading(true);

    const fetchTables = async () => {
      try {
        const res = await axios.get(url, {
          headers: { Authorization: token },
        });

        if (!res.data.success) {
          const message =
            res.data.error || t("qrCodeEditModal.failedToFetchTables");
          toast.error(message);
          return;
        }
        setTables(res.data.tables);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            t("qrCodeEditModal.failedToFetchTables");
          toast.error(message);
        } else {
          console.error(error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTables();
  }, [BASE_URL, lang, selectedBranch, t, token]);

  return (
    <div>
      <div className="flex items-center justify-start gap-4">
        <button
          onClick={handleOpenModal}
          className="self-end rounded-md border border-sidebar px-2 py-1 text-sm font-medium capitalize text-sidebar md:px-3 md:py-1 md:text-base"
        >
          {t("cashier.chooseTable")}
        </button>

        {selectedTable && (
          <div className="flex items-center justify-center gap-2">
            <p className="font-semibold">{`${t("tableCard.floorLabel", { floor: ordinalFloor })} - ${t("tableCard.tableNumber", { number: selectedTable?.number })}`}</p>

            <button
              type="button"
              className="mt-[3px] rounded-md p-1 text-red-600 hover:bg-black/10"
            >
              <IoMdClose size={22} onClick={() => setSelectedTable(null)} />
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div
            ref={modalRef}
            className="flex max-h-[85vh] w-[95%] max-w-3xl flex-col rounded-lg bg-white shadow-lg"
          >
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b bg-white px-4 py-3 sm:px-6">
              <div className="flex items-center gap-3">
                <MdTableBar className="text-xl" />
                <h2 className="text-lg font-semibold">
                  {t("cashier.chooseTable")}
                </h2>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-red-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {isLoading ? (
                <div className="mx-auto grid max-w-4xl grid-cols-2 justify-center gap-5 sm:grid-cols-3 md:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      className="h-[160px] w-full dark:bg-slate-600 dark:bg-opacity-35"
                    />
                  ))}
                </div>
              ) : !tables?.length ? (
                <Alert className="border-none bg-skyblue text-sidebar dark:border-none dark:bg-skyblue dark:text-sidebar">
                  <AlertTitle className="text-center text-xl">
                    {t("tableList.noTablesFound")}
                  </AlertTitle>
                  <AlertDescription className="my-5 text-center font-semibold">
                    {t("tableList.emptyMessage")}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="mx-auto flex max-w-4xl flex-wrap justify-center gap-5">
                  {tables.map((table) => (
                    <CashierTableCard
                      key={table._id}
                      table={table}
                      floorNumber={table.floorNumber}
                      isSelected={table._id === modalSelectedTable?._id}
                      onSelect={() => handleTableSelect(table)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Sticky Footer */}
            {tables?.length && !isLoading ? (
              <div className="sticky bottom-0 flex justify-end rounded-b-lg border-t bg-white px-4 py-3 sm:px-6">
                <button
                  onClick={handleSubmit}
                  disabled={!modalSelectedTable}
                  className={`self-end rounded-md bg-sidebar px-4 py-2 text-sm font-medium capitalize text-skyblue disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-600 md:px-5 md:py-2 md:text-base`}
                >
                  {t("cashier.submit")}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

const CashierTableCard = ({
  table,
  floorNumber,
  isSelected,
  onSelect,
}: CashierTableCardProps) => {
  const { t } = useTranslation();

  // const ordinalFloor =
  //   i18n.language === "ar"
  //     ? ArabicOrdinal[Number(floorNumber) - 1] || floorNumber
  //     : EnglishOrdinal[Number(floorNumber) - 1] || floorNumber;

  return (
    <>
      <div
        onClick={table.occupied ? undefined : onSelect}
        className={`relative flex h-[225px] w-[150px] max-w-[46%] flex-col items-center justify-start gap-[50px] rounded-lg bg-skyblue py-[3px] pb-2 text-sidebar xs:w-[160px] ${table.occupied ? "cursor-not-allowed" : "cursor-pointer"} select-none flex-col items-center justify-start gap-[3px] rounded-lg border-2 border-border py-[3px] pb-2 text-sidebar ${
          isSelected ? "border-4 border-green-500 bg-green-50" : ""
        }`}
      >
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-center justify-between ">
            <div className="flex items-center">
              <LiaChairSolid size={30} />
              <span>{table.chairsCount}</span>
            </div>
            <div className="mt-1 flex items-center gap-1"></div>
          </div>
        </div>
        <p className={`flex-1 text-center text-3xl font-bold`}>
          {table.number}
        </p>

        <p className="mt-1 text-xl font-medium">
          {t("tableCard.floorLabel", { floor: floorNumber })}
        </p>

        {table.occupied && (
          <div className="absolute inset-0 flex cursor-not-allowed items-center justify-center bg-black bg-opacity-70 text-white">
            <p className="mt-10 self-start rounded-md text-lg font-semibold">
              {t("tableCard.statusOccupied")}
            </p>
          </div>
        )}
      </div>

      {/*  Old Table style  */}

      {/* <div
      onClick={table.occupied ? undefined : onSelect}
      className={`relative flex ${table.occupied ? "cursor-not-allowed" : "cursor-pointer"} select-none flex-col items-center justify-start gap-[3px] rounded-lg border-2 border-border py-[3px] pb-2 text-sidebar xs:w-[160px] ${
        isSelected ? "border-4 border-green-500 bg-green-50" : ""
      }`}
    >
      <p className="py-[1px]">
        {t("tableCard.floorLabel", { floor: ordinalFloor })}
      </p>

      <div className="relative">
        <img
          width={"140px"}
          height={"140px"}
          src="/images/placeholders/tableImage.svg"
          className="mb-2 aspect-square"
          alt="Table Image"
        />

        <span className="absolute left-1/2 top-1/2 min-w-[90px] max-w-[100px] translate-x-[calc(-50%+1px)] translate-y-[calc(-50%-4.5px)] rounded-lg bg-sidebar px-2 py-1 text-center text-base font-bold text-skyblue">
          {t("tableCard.chairsCount", { count: table.chairsCount })}
        </span>
      </div>

      <p className="w-[80%] rounded-lg bg-skyblue px-2 py-1 text-center text-lg font-bold">
        {t("tableCard.tableNumber", { number: table.number })}
      </p>

      
      {table.occupied && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 text-white">
          <p className="font-semibold rounded-md text-lg">
          {t("tableCard.statusOccupied")}
          </p>
        </div>
      )}
    </div> */}
    </>
  );
};

export default CashierTableList;
